import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Plus, Pencil, Trash2, Check, X, Star, 
  Loader2, Send, Server, Shield, Eye, EyeOff, TestTube
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { cuentasEmailAPI } from '../api';
import { toast } from '../hooks/use-toast';

const FORM_INITIAL = {
  nombre: '',
  email: '',
  descripcion: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_use_tls: true,
  smtp_use_ssl: false,
  imap_host: '',
  imap_port: 993,
  imap_user: '',
  imap_password: '',
  imap_use_ssl: true,
  pop_host: '',
  pop_port: 995,
  pop_user: '',
  pop_password: '',
  pop_use_ssl: true,
  es_principal: false,
  firma_html: '',
  firma_texto: ''
};

const ConfiguracionCuentasEmail = () => {
  const navigate = useNavigate();
  
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [form, setForm] = useState(FORM_INITIAL);
  
  const [showPasswords, setShowPasswords] = useState({
    smtp: false,
    imap: false,
    pop: false
  });
  
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testingCuenta, setTestingCuenta] = useState(null);
  const [testingSmtp, setTestingSmtp] = useState(false);

  useEffect(() => {
    loadCuentas();
  }, []);

  const loadCuentas = async () => {
    try {
      setLoading(true);
      const res = await cuentasEmailAPI.getAll();
      setCuentas(res.data);
    } catch (error) {
      console.error('Error loading cuentas:', error);
      toast({ title: "Error", description: "No se pudieron cargar las cuentas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingCuenta(null);
    setForm(FORM_INITIAL);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    setForm({
      nombre: cuenta.nombre || '',
      email: cuenta.email || '',
      descripcion: cuenta.descripcion || '',
      smtp_host: cuenta.smtp_host || '',
      smtp_port: cuenta.smtp_port || 587,
      smtp_user: cuenta.smtp_user || '',
      smtp_password: '',
      smtp_use_tls: cuenta.smtp_use_tls ?? true,
      smtp_use_ssl: cuenta.smtp_use_ssl ?? false,
      imap_host: cuenta.imap_host || '',
      imap_port: cuenta.imap_port || 993,
      imap_user: cuenta.imap_user || '',
      imap_password: '',
      imap_use_ssl: cuenta.imap_use_ssl ?? true,
      pop_host: cuenta.pop_host || '',
      pop_port: cuenta.pop_port || 995,
      pop_user: cuenta.pop_user || '',
      pop_password: '',
      pop_use_ssl: cuenta.pop_use_ssl ?? true,
      es_principal: cuenta.es_principal || false,
      firma_html: cuenta.firma_html || '',
      firma_texto: cuenta.firma_texto || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nombre || !form.email || !form.smtp_host || !form.smtp_user) {
      toast({ title: "Error", description: "Complete los campos obligatorios", variant: "destructive" });
      return;
    }
    
    try {
      setSaving(true);
      
      const data = { ...form };
      
      // Si editamos y no se cambió la password, no enviarla
      if (editingCuenta) {
        if (!data.smtp_password) delete data.smtp_password;
        if (!data.imap_password) delete data.imap_password;
        if (!data.pop_password) delete data.pop_password;
        
        await cuentasEmailAPI.update(editingCuenta.id, data);
        toast({ title: "Cuenta actualizada" });
      } else {
        if (!form.smtp_password) {
          toast({ title: "Error", description: "La contraseña SMTP es obligatoria", variant: "destructive" });
          return;
        }
        await cuentasEmailAPI.create(data);
        toast({ title: "Cuenta creada" });
      }
      
      setIsDialogOpen(false);
      loadCuentas();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo guardar",
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cuenta) => {
    if (!window.confirm(`¿Eliminar la cuenta ${cuenta.email}?`)) return;
    
    try {
      await cuentasEmailAPI.delete(cuenta.id);
      toast({ title: "Cuenta eliminada" });
      loadCuentas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  const handleSetPrincipal = async (cuenta) => {
    try {
      await cuentasEmailAPI.setPrincipal(cuenta.id);
      toast({ title: "Cuenta principal establecida" });
      loadCuentas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo establecer", variant: "destructive" });
    }
  };

  const handleToggleActivo = async (cuenta) => {
    try {
      await cuentasEmailAPI.update(cuenta.id, { activo: !cuenta.activo });
      toast({ title: cuenta.activo ? "Cuenta desactivada" : "Cuenta activada" });
      loadCuentas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo cambiar el estado", variant: "destructive" });
    }
  };

  const handleTestSmtp = async (cuenta) => {
    setTestingCuenta(cuenta);
    setTestingSmtp(true);
    
    try {
      const res = await cuentasEmailAPI.testSmtp(cuenta.id);
      if (res.data.success) {
        toast({ title: "Conexión exitosa", description: res.data.message });
        loadCuentas();
      } else {
        toast({ title: "Error de conexión", description: res.data.message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "No se pudo probar la conexión", variant: "destructive" });
    } finally {
      setTestingSmtp(false);
      setTestingCuenta(null);
    }
  };

  const handleOpenTestDialog = (cuenta) => {
    setTestingCuenta(cuenta);
    setTestEmailTo('');
    setIsTestDialogOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmailTo || !testingCuenta) return;
    
    try {
      setSaving(true);
      const res = await cuentasEmailAPI.sendTestEmail(testingCuenta.id, testEmailTo);
      
      if (res.data.success) {
        toast({ title: "Email enviado", description: res.data.message });
        setIsTestDialogOpen(false);
        loadCuentas();
      } else {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "No se pudo enviar el email", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/configuracion')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Configuración
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cuentas de Email</h1>
            <p className="text-gray-600 mt-1">Configura las cuentas de correo para enviar comunicaciones</p>
          </div>
          <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Nueva Cuenta
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : cuentas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay cuentas configuradas</h3>
            <p className="text-gray-500 mb-4">Añade cuentas de email para enviar comunicaciones a clientes</p>
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" /> Añadir primera cuenta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {cuentas.map((cuenta) => (
            <Card key={cuenta.id} className={!cuenta.activo ? 'opacity-60' : ''}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      cuenta.es_principal ? 'bg-yellow-100' : 'bg-blue-100'
                    }`}>
                      <Mail className={`w-6 h-6 ${cuenta.es_principal ? 'text-yellow-600' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{cuenta.nombre}</h3>
                        {cuenta.es_principal && (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            <Star className="w-3 h-3 mr-1" /> Principal
                          </Badge>
                        )}
                        {!cuenta.activo && (
                          <Badge variant="secondary">Inactiva</Badge>
                        )}
                        {cuenta.verificado_smtp && (
                          <Badge className="bg-green-100 text-green-700">
                            <Check className="w-3 h-3 mr-1" /> SMTP verificado
                          </Badge>
                        )}
                      </div>
                      <p className="text-blue-600 font-medium">{cuenta.email}</p>
                      {cuenta.descripcion && (
                        <p className="text-sm text-gray-500 mt-1">{cuenta.descripcion}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Server className="w-3 h-3" />
                          SMTP: {cuenta.smtp_host}:{cuenta.smtp_port}
                        </span>
                        {cuenta.imap_host && (
                          <span className="flex items-center gap-1">
                            IMAP: {cuenta.imap_host}:{cuenta.imap_port}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleTestSmtp(cuenta)}
                      disabled={testingSmtp && testingCuenta?.id === cuenta.id}
                    >
                      {testingSmtp && testingCuenta?.id === cuenta.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleOpenTestDialog(cuenta)}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    {!cuenta.es_principal && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSetPrincipal(cuenta)}
                        title="Establecer como principal"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleOpenEdit(cuenta)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleToggleActivo(cuenta)}
                    >
                      {cuenta.activo ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(cuenta)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCuenta ? 'Editar Cuenta de Email' : 'Nueva Cuenta de Email'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="general" className="mt-4">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="smtp">SMTP</TabsTrigger>
                <TabsTrigger value="imap">IMAP</TabsTrigger>
                <TabsTrigger value="pop">POP3</TabsTrigger>
              </TabsList>
              
              {/* General */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de la cuenta *</Label>
                    <Input
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="Ej: Info General"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="info@lexduo.cat"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Input
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Descripción de uso de esta cuenta"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.es_principal}
                    onCheckedChange={(v) => setForm({ ...form, es_principal: v })}
                  />
                  <Label>Cuenta principal (por defecto)</Label>
                </div>
                <div>
                  <Label>Firma (texto)</Label>
                  <Textarea
                    value={form.firma_texto}
                    onChange={(e) => setForm({ ...form, firma_texto: e.target.value })}
                    placeholder="Firma para emails en texto plano"
                    rows={3}
                  />
                </div>
              </TabsContent>
              
              {/* SMTP */}
              <TabsContent value="smtp" className="space-y-4 mt-4">
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <strong>SMTP</strong> - Configuración para enviar emails
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Servidor SMTP *</Label>
                    <Input
                      value={form.smtp_host}
                      onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                      placeholder="mail.lexduo.cat"
                      required
                    />
                  </div>
                  <div>
                    <Label>Puerto *</Label>
                    <Input
                      type="number"
                      value={form.smtp_port}
                      onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) })}
                      placeholder="587"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Usuario SMTP *</Label>
                    <Input
                      value={form.smtp_user}
                      onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                      placeholder="info@lexduo.cat"
                      required
                    />
                  </div>
                  <div>
                    <Label>Contraseña SMTP {editingCuenta ? '(dejar vacío para mantener)' : '*'}</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.smtp ? 'text' : 'password'}
                        value={form.smtp_password}
                        onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                        placeholder="••••••••"
                        required={!editingCuenta}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={() => setShowPasswords({ ...showPasswords, smtp: !showPasswords.smtp })}
                      >
                        {showPasswords.smtp ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.smtp_use_tls}
                      onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v, smtp_use_ssl: v ? false : form.smtp_use_ssl })}
                    />
                    <Label>Usar STARTTLS</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.smtp_use_ssl}
                      onCheckedChange={(v) => setForm({ ...form, smtp_use_ssl: v, smtp_use_tls: v ? false : form.smtp_use_tls })}
                    />
                    <Label>Usar SSL</Label>
                  </div>
                </div>
              </TabsContent>
              
              {/* IMAP */}
              <TabsContent value="imap" className="space-y-4 mt-4">
                <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                  <strong>IMAP</strong> - Configuración para recibir emails (opcional)
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Servidor IMAP</Label>
                    <Input
                      value={form.imap_host}
                      onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                      placeholder="imap.lexduo.cat"
                    />
                  </div>
                  <div>
                    <Label>Puerto</Label>
                    <Input
                      type="number"
                      value={form.imap_port}
                      onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) })}
                      placeholder="993"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Usuario IMAP</Label>
                    <Input
                      value={form.imap_user}
                      onChange={(e) => setForm({ ...form, imap_user: e.target.value })}
                      placeholder="Mismo que SMTP si se deja vacío"
                    />
                  </div>
                  <div>
                    <Label>Contraseña IMAP</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.imap ? 'text' : 'password'}
                        value={form.imap_password}
                        onChange={(e) => setForm({ ...form, imap_password: e.target.value })}
                        placeholder="Misma que SMTP si se deja vacío"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={() => setShowPasswords({ ...showPasswords, imap: !showPasswords.imap })}
                      >
                        {showPasswords.imap ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.imap_use_ssl}
                    onCheckedChange={(v) => setForm({ ...form, imap_use_ssl: v })}
                  />
                  <Label>Usar SSL</Label>
                </div>
              </TabsContent>
              
              {/* POP3 */}
              <TabsContent value="pop" className="space-y-4 mt-4">
                <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                  <strong>POP3</strong> - Configuración alternativa para recibir emails (opcional)
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Servidor POP3</Label>
                    <Input
                      value={form.pop_host}
                      onChange={(e) => setForm({ ...form, pop_host: e.target.value })}
                      placeholder="pop.lexduo.cat"
                    />
                  </div>
                  <div>
                    <Label>Puerto</Label>
                    <Input
                      type="number"
                      value={form.pop_port}
                      onChange={(e) => setForm({ ...form, pop_port: parseInt(e.target.value) })}
                      placeholder="995"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Usuario POP3</Label>
                    <Input
                      value={form.pop_user}
                      onChange={(e) => setForm({ ...form, pop_user: e.target.value })}
                      placeholder="Mismo que SMTP si se deja vacío"
                    />
                  </div>
                  <div>
                    <Label>Contraseña POP3</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.pop ? 'text' : 'password'}
                        value={form.pop_password}
                        onChange={(e) => setForm({ ...form, pop_password: e.target.value })}
                        placeholder="Misma que SMTP si se deja vacío"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={() => setShowPasswords({ ...showPasswords, pop: !showPasswords.pop })}
                      >
                        {showPasswords.pop ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.pop_use_ssl}
                    onCheckedChange={(v) => setForm({ ...form, pop_use_ssl: v })}
                  />
                  <Label>Usar SSL</Label>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingCuenta ? 'Guardar cambios' : 'Crear cuenta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de enviar email de prueba */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Email de Prueba</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Desde:</strong> {testingCuenta?.email}
              </p>
            </div>
            <div>
              <Label>Enviar a *</Label>
              <Input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="destinatario@ejemplo.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSendTestEmail} 
              disabled={saving || !testEmailTo}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Send className="w-4 h-4 mr-2" />
              Enviar prueba
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionCuentasEmail;
